import process from "process";
import { HttpTransportType, HubConnectionBuilder, HubConnectionState, LogLevel } from "redux-signalr";

const hubUrl = `${process.env.BACKEND_ROOT}/hub`;

const isSafari = (() => {
	const userAgent = navigator.userAgent.toLowerCase();
	return !userAgent.includes('chrome')
	&& (
		userAgent.includes('safari')
		|| userAgent.includes('webkit')
		|| userAgent.includes('mobile safari')
		|| userAgent.includes('crios')
		|| userAgent.includes('fxios')
		|| userAgent.includes('duckduckgo')
	);
})();

const isValidJwtToken = (token: string): boolean => {
	if (!token || token.trim() === "") return false;
	
	try {
		const parts = token.split(".");
		if (parts.length !== 3 || !parts[1]) return false;
		
		const payload = JSON.parse(atob(parts[1]));
		const now = Math.floor(Date.now() / 1000);

		const bufferTime = 300;
		return payload.exp && payload.exp > (now + bufferTime);
	} catch {
		return false;
	}
};

const getSafariOptimizedOptions = () => {
	if (!isSafari) {
		return {
			skipNegotiation: true,
			transport: HttpTransportType.WebSockets,
		};
	}

	return {
		skipNegotiation: true,
		transport: HttpTransportType.WebSockets,
		headers: {
			"Cache-Control": "no-cache, no-store, must-revalidate",
			Pragma: "no-cache",
			Expires: "0"
		},
		timeout: 30000,
		withCredentials: false
	};
};

let isConnecting = false;
let connectionPromise: Promise<void> | null = null;

const connection = new HubConnectionBuilder()
	.configureLogging(isSafari ? LogLevel.Information : LogLevel.Debug)
	.withUrl(hubUrl, {
		...getSafariOptimizedOptions(),
		accessTokenFactory: async () => {
			try {
				const jwtItem = localStorage.getItem("user_jwt");
				
				if (!jwtItem || jwtItem.trim() === "") {
					console.warn("No authentication token available for SignalR connection");
					if (isSafari) {
						throw new Error("No authentication token available");
					}
					return "";
				}
				
				if (!isValidJwtToken(jwtItem)) {
					console.warn("Invalid or expired JWT token for SignalR connection");
					if (isSafari) {
						const refreshToken = localStorage.getItem("user_refresh");
						if (refreshToken) {
							console.log("Attempting token refresh for Safari before SignalR connection");
						}
						throw new Error("Invalid or expired JWT token");
					}
					return "";
				}
				
				return jwtItem;
			} catch (error) {
				console.error("Error in accessTokenFactory:", error);
				throw error;
			}
		}
	})
	.withAutomaticReconnect(isSafari
		? [0, 1000, 3000, 5000, 10000, 15000, 30000]
		: [0, 2000, 10000, 30000])
	.build();

const EnsureConnectionMade = async () => {
	if (isConnecting) {
		console.log("SignalR connection attempt already in progress, waiting...");
		return connectionPromise;
	}

	if (isSafari && document.visibilityState !== 'visible') {
		console.log("Page not visible, skipping SignalR connection for Safari");
		return;
	}

	const jwtItem = localStorage.getItem("user_jwt");
	if (!jwtItem || jwtItem.trim() === "") {
		console.warn("Cannot establish SignalR connection: No authentication token");
		return;
	}
	
	if (!isValidJwtToken(jwtItem)) {
		console.warn("Cannot establish SignalR connection: Invalid or expired token");
		return;
	}

	if (connection.state === HubConnectionState.Disconnected) {
		isConnecting = true;
		
		connectionPromise = (async () => {
			try {
				if (isSafari) {
					await new Promise<void>((resolve) => {
						setTimeout(() => resolve(), 100);
					});
					
					const freshToken = localStorage.getItem("user_jwt");
					if (!freshToken || !isValidJwtToken(freshToken)) {
						throw new Error("Token became invalid during Safari connection attempt");
					}
				}

				await connection.start();
				console.log(`SignalR connection established${isSafari ? ' (Safari optimized)' : ''}`);
			} catch (error: any) {
				console.error("Failed to start SignalR connection:", error);
				
				if (error?.message?.includes("Handshake was canceled")
					|| error?.message?.includes("handshake")
					|| error?.message?.includes("unauthorized")) {
					console.warn(`Handshake failed${isSafari ? ' (Safari)' : ''} - token may be invalid or expired`);
					
					if (isSafari) {
						console.log("Safari handshake failed, implementing delay before next attempt");
						await new Promise<void>((resolve) => {
							setTimeout(() => resolve(), 2000);
						});
					}
				}
				throw error;
			} finally {
				isConnecting = false;
				connectionPromise = null;
			}
		})();

		return connectionPromise;
	}
};

connection.onclose((error) => {
	console.error(`SignalR connection closed${isSafari ? ' (Safari)' : ''}. Error:`, error);

	if (error?.message?.includes("handshake")
		|| error?.message?.includes("unauthorized")
		|| error?.message?.includes("Handshake was canceled")) {
		console.warn(`Authentication/handshake error${isSafari ? ' (Safari)' : ''} - not attempting reconnect`);
		return;
	}

	const jwtItem = localStorage.getItem("user_jwt");
	if (!jwtItem || jwtItem.trim() === "") {
		console.warn("No valid token available - not attempting reconnect");
		return;
	}

	const reconnectDelay = isSafari ? 8000 : 5000;
	const safariOptimized = isSafari ? ' (Safari optimized)' : '';
	console.log(`Attempting to reconnect in ${reconnectDelay / 1000} seconds...${safariOptimized}`);
	
	setTimeout(() => {
		if (isSafari && document.visibilityState !== 'visible') {
			console.log("Page not visible during Safari reconnect attempt, skipping");
			return;
		}
		EnsureConnectionMade();
	}, reconnectDelay);
});
	
connection.onreconnecting((error) => {
	console.warn(`SignalR reconnecting${isSafari ? ' (Safari)' : ''}. Error:`, error);
});
	
connection.onreconnected((connectionId) => {
	console.log(`SignalR reconnected${isSafari ? ' (Safari)' : ''}. Connection ID:`, connectionId);
});

connection.on("WentToQuote", (quoteId) => {
	console.log("Went to quote: ", quoteId);
});

connection.on("RequestPageReload", (quoteId) => {
	console.log(`Received request to reload page for quote: ${quoteId}`);
	window.location.reload();
});

if (isSafari) {
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible') {
			console.log('Safari page became visible, checking SignalR connection...');
			if (connection.state === HubConnectionState.Disconnected) {
				const jwtItem = localStorage.getItem("user_jwt");
				if (jwtItem && isValidJwtToken(jwtItem)) {
					setTimeout(() => EnsureConnectionMade(), 1000);
				}
			}
		} else {
			console.log('Safari page became hidden, SignalR may be affected');
		}
	});
}

// // store: any, callback: (() => void)
// export function signalRStart() {
// 	const _hub = connection;

// 	// _hub.invoke .firstClientFunction = (p1: any) => {
// 	// 	store.dispatch({ type: "SERVER_CALLED_ME", a: p1 });
// 	// }

// 	// _hub.start(() => callback());
// }

export {
	EnsureConnectionMade
};

export default connection;
